import { sectionToEditorHtml } from "./lessonOutlineApply";
import { getTipTapBlocksForSectionWithFallback } from "./lessonAiContentBlocks";

// Article-text noise cleanup (ARTICLE_NOISE / cleanArticleText /
// plainTextToParagraphHtml) used to live here so the raw teacherMaterial
// could be prepended to the reading section. That prepend was removed —
// the AI now owns the reading excerpt for both ORIGINAL and ADAPTED
// modes — and the equivalent cleanup runs server-side in
// lib/lessonAiFetchSource.cjs (cleanArticleNoise) before the model
// ever sees the source.

// ─── Section metadata ─────────────────────────────────────────────────────────

export const SECTION_META = {
  leadin:     { label: 'Warm-up',    color: '#6d28d9' },
  reading:    { label: 'Reading',    color: '#1e40af' },
  vocabulary: { label: 'Vocabulary', color: '#065f46' },
  grammar:    { label: 'Grammar',    color: '#92400e' },
  speaking:   { label: 'Speaking',   color: '#0c4a6e' },
  listening:  { label: 'Listening',  color: '#134e4a' },
  homework:   { label: 'Homework',   color: '#1f2937' },
};

export function inferSectionType(sec) {
  const t = String(sec.title || '').toLowerCase();
  const bt = String(sec.blockType || '').toLowerCase();
  if (bt === 'homework' || t === 'homework' || t === 'writing') return 'homework';
  if (bt === 'main' || t.includes('warm') || t.includes('lead')) return 'leadin';
  if (t === 'vocabulary' || t.includes('vocab') || t.includes('lexis')) return 'vocabulary';
  if (t === 'grammar' || t.includes('grammar')) return 'grammar';
  if (t === 'speaking' || t === 'discussion' || t.includes('speak')) return 'speaking';
  if (t.includes('listen')) return 'listening';
  return 'reading';
}

// ─── Multi-container patch ────────────────────────────────────────────────────

/**
 * Returns one container object per outline section, each tagged with section
 * metadata so LessonContainer can render a themed header.
 */
export function outlineToMainTabWorkspacePatch(outline) {
  const sections = Array.isArray(outline?.sections) ? outline.sections : [];
  const base = Date.now();

  if (!sections.length) {
    const id = base;
    return {
      containers: [{ id, type: 'editor' }],
      editorContentsPatch: { [`container-${id}`]: '<p></p>' },
    };
  }

  const containers = sections.map((sec, i) => {
    const sectionType = inferSectionType(sec);
    const meta = SECTION_META[sectionType] || SECTION_META.reading;
    return {
      id: base + i,
      type: 'editor',
      sectionType,
      sectionTitle: String(sec.title || meta.label).trim(),
      sectionColor: meta.color,
      sectionMinutes: Number(sec.estimatedMinutes) || 0,
    };
  });

  const editorContentsPatch = Object.fromEntries(
    containers.map(c => [`container-${c.id}`, '<p></p>'])
  );

  return { containers, editorContentsPatch };
}

// ─── Per-section insert ───────────────────────────────────────────────────────

/**
 * Insert each outline section into its own container editor.
 *
 * @param {object}   outline
 * @param {function} getEditorByContainerId  (id) => TipTap editor | null
 * @param {number[]} sectionContainerIds     one container id per section
 * @param {object}   applyMeta              { teacherMaterial, mode }
 */
export function scheduleInterleavedOutlineInsert(
  outline,
  getEditorByContainerId,
  sectionContainerIds,
  applyMeta = {}
) {
  const sections = Array.isArray(outline?.sections) ? outline.sections : [];
  const lessonTitle = String(outline?.lessonTitle || '').trim();
  const cefr = outline?.cefrLevel || 'B1';
  if (!sections.length) return;

  const youtubeUrl = applyMeta?.youtubeUrl || null;

  // Normalise: support both single id (legacy) and array
  const ids = Array.isArray(sectionContainerIds)
    ? sectionContainerIds
    : sections.map(() => sectionContainerIds);

  // Source text belongs in the reading section, falling back to the first section
  const readingIdx = sections.findIndex(s => inferSectionType(s) === 'reading');
  const sourceTargetIdx = readingIdx !== -1 ? readingIdx : 0;

  // YouTube embed goes into listening section, then reading, then first
  const listeningIdx = sections.findIndex(s => inferSectionType(s) === 'listening');
  const youtubeTargetIdx = listeningIdx !== -1 ? listeningIdx : sourceTargetIdx;

  sections.forEach((sec, i) => {
    const containerId = ids[i];
    let attempt = 0;

    const init = () => {
      const editor = getEditorByContainerId(containerId);
      if (!editor || typeof editor.chain !== 'function') {
        attempt++;
        if (attempt < 75) setTimeout(init, 150);
        return;
      }

      // YouTube embed — set as initial content of the listening section
      if (i === youtubeTargetIdx && youtubeUrl) {
        const videoIdMatch = youtubeUrl.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
        const embedSrc = videoIdMatch
          ? `https://www.youtube.com/embed/${videoIdMatch[1]}`
          : youtubeUrl;
        editor.commands.setContent({
          type: 'doc',
          content: [
            { type: 'youtube', attrs: { src: embedSrc, width: 560, height: 315 } },
            { type: 'paragraph' },
          ],
        });
      } else {
        editor.chain().focus().clearContent().run();
      }

      // (removed) raw teacherMaterial prepend — both 'original' and
      // 'adapted' modes now rely on the AI's reading section to be the
      // single source of truth for the article text. Prepending the raw
      // teacherMaterial here was duplicating the excerpt at the top of
      // every lesson regardless of textMode.

      // Prose
      const prose = sectionToEditorHtml(sec, lessonTitle);
      editor.chain().insertContentAt(editor.state.doc.content.size, prose).run();

      // Exercises — after prose settles
      setTimeout(() => {
        const ed = getEditorByContainerId(containerId);
        if (!ed || typeof ed.chain !== 'function') return;
        const blocks = getTipTapBlocksForSectionWithFallback(sec, cefr, { lessonTitle });
        if (!blocks.length) {
          console.log('[smartLesson] no blocks to insert for section:', sec.title);
          return;
        }
        console.log('[smartLesson] inserting', blocks.length, 'blocks into', sec.title, '→', blocks.map(b => b.type).join(', '));
        // Insert a leading paragraph once, then each block separately —
        // this way a single malformed block does not drop the rest.
        try {
          ed.chain().insertContentAt(ed.state.doc.content.size, { type: 'paragraph' }).run();
        } catch (_) {}
        blocks.forEach((b, bi) => {
          try {
            ed.chain()
              .insertContentAt(ed.state.doc.content.size, { type: b.type, attrs: b.attrs })
              .run();
          } catch (e) {
            console.warn('[smartLesson] block insert error', sec.title, '#' + bi, b.type, e?.message);
          }
        });
      }, 250);
    };

    // Stagger container inits slightly so React has time to mount each editor
    setTimeout(init, 150 + i * 80);
  });
}

// ─── Legacy block inserter ────────────────────────────────────────────────────

export function scheduleInsertBlocksForOutline(blocksPlan, getEditorByContainerId) {
  if (!blocksPlan?.length) return;
  blocksPlan.forEach((row, i) => {
    const { containerId, blocks } = row;
    if (!blocks?.length) return;
    let attempt = 0;
    const run = () => {
      const editor = getEditorByContainerId(containerId);
      if (editor && typeof editor.chain === 'function') {
        try {
          editor.chain().focus('end')
            .insertContent(blocks.map(b => ({ type: b.type, attrs: b.attrs })))
            .run();
        } catch (e) {
          console.warn('[smartLesson] scheduleInsertBlocksForOutline error', e);
        }
        return;
      }
      attempt++;
      if (attempt < 70) setTimeout(run, 120 + i * 50);
    };
    setTimeout(run, 120 + i * 50);
  });
}
